import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, Tag, Rss, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import AuthorAvatar from "../../components/AuthorAvatar";
import SEO from "../../components/SEO";

/**
 * Blog index — public, SEO-critical. Lists all published posts newest-first.
 * Self-contained (no DashboardShell) so it renders cleanly for anonymous
 * visitors arriving from a Google snippet.
 */
export default function BlogIndex() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api
      .get("/blog/posts")
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => setPosts([]));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Blog — Playbooks for Commercial Real-Estate Investors"
        description="Practical playbooks on refinancing, AI lease compliance, multi-LLC operations, and CRE portfolio management from real operators."
        path="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "RealtyVault Manager Blog",
          url: "https://realtyvaultmgr.com/blog",
          description: "Field notes for commercial real-estate investors.",
          publisher: {
            "@type": "Organization",
            name: "RealtyVault Manager",
            url: "https://realtyvaultmgr.com",
          },
          ...(posts && posts.length > 0
            ? {
                blogPost: posts.slice(0, 10).map((p) => ({
                  "@type": "BlogPosting",
                  headline: p.title,
                  description: p.description,
                  url: `https://realtyvaultmgr.com/blog/${p.slug}`,
                  datePublished: p.date,
                  author: { "@type": "Person", name: p.author },
                })),
              }
            : {}),
        }}
      />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-bold text-lg text-slate-900 tracking-tight" style={{ fontFamily: "Outfit" }}>
            RealtyVault Manager
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/blog" className="font-semibold text-slate-900">Blog</Link>
            <Link to="/login" className="text-slate-600 hover:text-slate-900">Sign in</Link>
            <Link to="/signup" className="bg-slate-900 text-white px-3 py-1.5 rounded-sm font-semibold text-xs hover:bg-slate-700">Start free trial</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="mb-10">
          <div className="overline text-emerald-700">RealtyVault blog</div>
          <h1
            className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-1"
            style={{ fontFamily: "Outfit" }}
          >
            Field notes for commercial real-estate investors
          </h1>
          <p className="text-base text-slate-600 mt-3 max-w-2xl">
            Practical playbooks on refinancing, AI lease compliance, multi-LLC operations, and the
            tools we wish existed when we started managing a portfolio.
          </p>
          <a
            href="/api/blog/rss.xml"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-emerald-700 mt-4"
            data-testid="blog-rss-link"
          >
            <Rss className="w-3.5 h-3.5" /> Subscribe via RSS
          </a>
        </div>

        {posts === null && <div className="text-slate-400">Loading…</div>}
        {posts && posts.length === 0 && (
          <div className="text-slate-400 italic">No posts yet.</div>
        )}

        <ul className="space-y-8" data-testid="blog-post-list">
          {posts?.map((p) => (
            <li
              key={p.slug}
              className="bg-white border border-slate-200 hover:border-slate-900 rounded-sm p-6 transition-colors"
              data-testid={`blog-post-card-${p.slug}`}
            >
              <Link to={`/blog/${p.slug}`} className="block">
                <h2
                  className="text-2xl font-bold tracking-tight text-slate-900 hover:text-emerald-700 transition-colors"
                  style={{ fontFamily: "Outfit" }}
                >
                  {p.title}
                </h2>
                <p className="text-sm text-slate-600 mt-2">{p.description}</p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-3">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {formatDate(p.date)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {p.reading_time_min} min read
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <AuthorAvatar author={p.author_profile || { name: p.author }} size="sm" />
                    by <span className="font-medium text-slate-700">{p.author}</span>
                  </span>
                </div>
                {p.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {p.tags.map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-0.5 rounded-sm font-semibold"
                      >
                        <Tag className="w-2.5 h-2.5" /> {t}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700">
                  Read article <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </main>

      <footer className="border-t border-slate-200 bg-white mt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
          <span>© 2026 RealtyVault Manager</span>
          <Link to="/" className="hover:text-slate-900">Home</Link>
        </div>
      </footer>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
